import { memo } from "react"

import {
  ANNOTATION_VALUE,
  type AnnotationValue,
} from "@/constants/annotation-values"
import {
  useHumanFeedbackUpdate,
  useReviewAnnotationMode,
  useReviewSubjectAnnotation,
} from "@/human-feedback/annotations"
import { AIReviewButton, AnnotationSelector } from "@/metrics/metric-display"
import {
  supportsModelSelection,
  supportsTraceContext,
  supportsTranscriptFilter,
} from "@/metrics/runtime"
import type { Annotation } from "@/types"
import { Tooltip } from "@coval/ui/tooltip"

import type { TestCaseRecord } from "@/types/api/TestCaseRecord"
import type {
  MetricRecordData,
  TargetCondition,
} from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"
import { renderQuestionWithAttributes } from "@/utils/backend-mimics/renderQuestionWithAttributes"
import type { AttributeContext } from "@/utils/backend-mimics/replace_with_attributes"
import { cn } from "@/utils/cn"
import {
  getKnowledgeBaseEntries,
  getMetricConfidence,
  getMetricExecutionQuestion,
  getMetricResultString,
  getMetricRuntimeString,
  type KnowledgeBaseEntryLike,
} from "@/utils/metric-output-boundaries"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

import { KnowledgeBaseDisableControl } from "../../configuration/components/KnowledgeBaseDisableControl"
import { LLMModelSelect } from "../../configuration/components/LLMModelSelect"
import { QuestionTextarea } from "../../configuration/components/QuestionTextarea"
import { TargetFilterToggle } from "../../configuration/components/TargetFilterToggle"
import { TraceContextToggle } from "../../configuration/trace-context"
import {
  TranscriptFilterDetails,
  TranscriptFilterToggle,
} from "../../configuration/transcript-filter"
import { BinaryMetricRecord } from "../base/BinaryMetricRecord"
import { BinaryHumanFeedbackButton } from "../helpers/BinaryHumanFeedbackButton"
import { appendJudgeConfidence } from "../judge-confidence/appendJudgeConfidence"
import { CovalJudgeConfidenceInfo } from "../judge-confidence/CovalJudgeConfidenceInfo"
import { getJudgeConfidence } from "../judge-confidence/judgeConfidence"

type BaseTestCaseRecord = Pick<
  TestCaseRecord,
  "input_str" | "expected_behaviors" | "expected_output_json"
>

interface HumanReviewInputWrapperProps {
  metricId: string
  simulationOutputId: string
  annotations_for_metric: Annotation[]
  projectId?: string
  targetCondition?: TargetCondition
}

const HumanReviewInputWrapper: React.FC<HumanReviewInputWrapperProps> = memo(
  ({
    metricId,
    simulationOutputId,
    annotations_for_metric,
    projectId,
    targetCondition,
  }: HumanReviewInputWrapperProps) => {
    const { isReadOnly } = useReviewAnnotationMode()
    const my_annotation = useReviewSubjectAnnotation(annotations_for_metric)
    const { currentAnnotation, handleValueUpdate } = useHumanFeedbackUpdate({
      simulationOutputId,
      metricId,
      initialAnnotation: my_annotation || null,
      projectId,
    })

    return (
      <AnnotationSelector
        selectedValue={
          currentAnnotation?.ground_truth_string_value as AnnotationValue
        }
        onValueChange={(value) => {
          void handleValueUpdate(null, value, null)
        }}
        metricId={metricId}
        targetCondition={targetCondition}
        readOnly={isReadOnly}
      />
    )
  },
)
HumanReviewInputWrapper.displayName = "HumanReviewInputWrapper"

export class BinaryLLMMetricRecord extends BinaryMetricRecord {
  documentation = this.metric_metadata?.question || this.description
  supportsBinaryAggregateOverview = true as const

  protected _hasValidDeepDiveData(mo: RunMetricOutput): boolean {
    // Abstentions retain diagnostic evidence without becoming scoreable values.
    return (
      super._hasValidDeepDiveData(mo) ||
      getJudgeConfidence(mo, { binary: true }) !== null
    )
  }

  get hasVersionedConfig() {
    return true
  }

  isMetricOutputCurrentVersion(metricOutput: {
    result?: Record<string, unknown> | null
    result_json?: Record<string, unknown> | null
  }): boolean {
    const currentQuestion = this.metric_metadata?.question || ""
    const executionQuestion = getMetricExecutionQuestion(metricOutput)
    if (!currentQuestion || !executionQuestion) {
      return true
    }
    return currentQuestion === executionQuestion
  }

  getVersionDiff(metricOutput: {
    result?: Record<string, unknown> | null
    result_json?: Record<string, unknown> | null
  }): Array<{ field: string; current: string; previous: string }> {
    const diffs: Array<{ field: string; current: string; previous: string }> =
      []
    const currentQuestion = this.metric_metadata?.question || ""
    const executionQuestion = getMetricExecutionQuestion(metricOutput)
    if (
      currentQuestion &&
      executionQuestion &&
      currentQuestion !== executionQuestion
    ) {
      diffs.push({
        field: "Question",
        current: currentQuestion,
        previous: executionQuestion,
      })
    }
    return diffs
  }

  getExplanationTable(_runRows: RunRowLike[]) {
    const key = "explanation_0"
    return {
      columns: [{ key, title: "Explanation", width: "300px" }],
      getRowValues: (runRow: RunRowLike): Record<string, string> => {
        const mo = runRow.metric_outputs?.[this.id]
        const value = mo ? getMetricResultString(mo, "explanation") : undefined
        return {
          [key]: value ?? "",
        }
      },
    }
  }

  getOverviewValue(mo: RunMetricOutput[]): string | null {
    const valid = mo.filter(
      (m) =>
        m.string_value === ANNOTATION_VALUE.YES ||
        m.string_value === ANNOTATION_VALUE.NO,
    )
    if (valid.length === 0) {
      return null
    }

    const average =
      valid
        .map((m) =>
          m.string_value === ANNOTATION_VALUE.YES
            ? 1
            : m.string_value === ANNOTATION_VALUE.NO
              ? 0
              : -1,
        )
        .reduce((a: number, b: number) => a + b, 0) / valid.length
    return (average * 100).toFixed(0)
  }

  _replaceParameters(
    question: string,
    testCase: BaseTestCaseRecord,
  ): React.ReactNode {
    if (!question || !testCase?.expected_output_json) {
      return question
    }
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    const regex = /\{\{expected_output\.([a-zA-Z0-9_]+)\}\}/g
    let match
    let keyIndex = 0

    while ((match = regex.exec(question)) !== null) {
      if (match.index > lastIndex) {
        parts.push(question.substring(lastIndex, match.index))
      }

      const key = match[1]
      const originalTemplate = match[0]
      const hasReplacement =
        testCase.expected_output_json && key in testCase.expected_output_json

      if (hasReplacement) {
        const replacedValue = stringifyUnknownValue(
          testCase.expected_output_json[key],
        )
        parts.push(
          <Tooltip
            key={`param-${keyIndex}`}
            content={
              <div className="px-1 py-0.5">
                <div className="text-xs font-mono text-coval-text-muted">
                  {originalTemplate}
                </div>
              </div>
            }
          >
            <span className="bg-coval-info-surface text-coval-info-on-surface px-1.5 py-0.5 rounded font-medium border border-coval-info-border">
              {replacedValue}
            </span>
          </Tooltip>,
        )
        keyIndex++
      } else {
        parts.push(originalTemplate)
      }

      lastIndex = regex.lastIndex
    }

    if (lastIndex < question.length) {
      parts.push(question.substring(lastIndex))
    }

    return parts.length > 0 ? <>{parts}</> : question
  }

  renderCurrentQuestion(
    testCase: BaseTestCaseRecord,
    callContext?: AttributeContext,
  ): React.ReactNode | null {
    const question = this.metric_metadata?.question || ""
    if (!question) {
      return null
    }
    // Human Review passes the full call context so every prefix
    // ({{agent.*}}, {{customer_metadata.*}}, {{expected_output.*}}, ...) is
    // injected from the actual call. Without it (non-HR callers), fall back to
    // the local expected_output-only substitution with chip rendering.
    if (callContext) {
      return renderQuestionWithAttributes(question, callContext)
    }
    return this._replaceParameters(question, testCase)
  }

  _renderDeepDive(
    mo: RunMetricOutput,
    testCase: BaseTestCaseRecord,
  ): React.ReactNode {
    const currentQuestionStr = this.metric_metadata?.question || ""
    const currentQuestionRendered = this._replaceParameters(
      currentQuestionStr,
      testCase,
    )

    const executionQuestionStr =
      getMetricExecutionQuestion(mo) ??
      getMetricRuntimeString(mo, "user_provided_prompt")
    const userProvidedPrompt = executionQuestionStr || currentQuestionRendered

    const isUsingOldVersion =
      currentQuestionStr &&
      executionQuestionStr &&
      currentQuestionStr !== executionQuestionStr

    const isKnowledgeBaseEnabled = this.metric_metadata?.use_knowledge_base
    const {
      connectedEntries: kbEntriesUsed,
      retrievedEntries: allKbEntriesRetrieved,
    } = getKnowledgeBaseEntries(mo)

    const kbEntriesRetrieved = allKbEntriesRetrieved?.filter(
      (retrieved: KnowledgeBaseEntryLike) => {
        const retrievedId =
          retrieved.entry_id || retrieved.kb_identifier || retrieved.id
        return !kbEntriesUsed?.some((used: KnowledgeBaseEntryLike) => {
          const usedId = used.entry_id || used.kb_identifier || used.id
          return retrievedId === usedId
        })
      },
    )

    const getEntryTitle = (
      entry: KnowledgeBaseEntryLike,
    ): string | undefined => {
      const isShelf = entry.source === "shelf"

      if (isShelf) {
        return entry.title || entry.id
      }
      return entry.entry_name || entry.title || entry.entry_id || entry.id
    }

    const renderKBEntry = (
      entry: KnowledgeBaseEntryLike,
      isPrimary = false,
    ) => {
      const title = getEntryTitle(entry)
      const isShelf = entry.source === "shelf"
      const entryType = isShelf ? "Shelf" : "KB"
      const shouldLinkShelf = isShelf && mo.organization_id === "194336a3"
      const shelfUrl = shouldLinkShelf
        ? `https://chime.shelf.io/read/${entry.id as string}?searchEventId=01K986V4TTAY5T8SMY5RBNCCN9&parentId=4b79564b-aa8c-4364-95a6-4bfd9489ba3b&view=snippet-view&trigger=title&source=shelf`
        : null

      return (
        <div
          key={entry.entry_id || entry.kb_identifier || entry.id}
          className={cn(
            "flex items-center gap-3 p-3 rounded-lg border transition-colors",
            isPrimary
              ? "border-coval-info-border bg-coval-info-surface"
              : "border-border bg-muted/20 hover:bg-muted/30",
          )}
        >
          <div className="shrink-0">
            <div
              className={cn(
                "w-2 h-2 rounded-full",
                isPrimary ? "bg-coval-info-on-surface" : "bg-coval-text-muted",
              )}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm text-coval-text">
                {title}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-coval-info-surface text-coval-info-on-surface border border-coval-info-border">
                {entryType}
              </span>
              {shelfUrl && (
                <Tooltip content="Go to Shelf article">
                  <a
                    href={shelfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "hover:underline transition-colors",
                      isPrimary
                        ? "text-coval-info-on-surface"
                        : "text-coval-info-text",
                    )}
                    aria-label="Go to Shelf article"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </a>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-8">
        {/* Question and Explanation in Columns */}
        <div className="grid grid-cols-2 gap-6">
          {userProvidedPrompt && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-coval-text">
                  Question
                </h3>
              </div>

              <div className="relative">
                <div className="max-h-[200px] overflow-y-auto">
                  <div
                    className={cn(
                      "text-sm leading-relaxed whitespace-pre-wrap break-words",
                      isUsingOldVersion && "text-coval-warning-text",
                    )}
                  >
                    {userProvidedPrompt}
                  </div>
                </div>

                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-linear-to-t from-surface-default to-transparent" />
              </div>
            </div>
          )}

          {(() => {
            const explanation = getMetricResultString(mo, "explanation")

            if (!explanation) {
              return null
            }

            return (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-coval-text">
                  Explanation
                </h3>
                <div className="text-sm whitespace-pre-wrap break-words">
                  {explanation}
                </div>
              </div>
            )
          })()}
        </div>

        {/* Knowledge Base Sections */}
        {isKnowledgeBaseEnabled && (
          <div className="space-y-6">
            {/* Primary Articles Referenced */}
            {kbEntriesUsed && kbEntriesUsed.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-coval-text">
                  Primary Articles Referenced
                </h3>
                <div className="space-y-2">
                  {kbEntriesUsed.map((entry: KnowledgeBaseEntryLike) =>
                    renderKBEntry(entry, true),
                  )}
                </div>
              </div>
            )}

            {/* Articles Considered */}
            {kbEntriesRetrieved && kbEntriesRetrieved.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-coval-text">
                  Articles Considered
                </h3>
                <div className="space-y-2">
                  {kbEntriesRetrieved.map((entry: KnowledgeBaseEntryLike) =>
                    renderKBEntry(entry, false),
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  getDeepDiveSections(
    mo: RunMetricOutput,
    testCase: BaseTestCaseRecord,
  ): { label: string; content: React.ReactNode }[] | null {
    const sections: { label: string; content: React.ReactNode }[] = []

    const currentQuestionStr = this.metric_metadata?.question || ""
    const currentQuestionRendered = this._replaceParameters(
      currentQuestionStr,
      testCase,
    )
    const executionQuestionStr =
      getMetricExecutionQuestion(mo) ??
      getMetricRuntimeString(mo, "user_provided_prompt")
    const userProvidedPrompt = executionQuestionStr || currentQuestionRendered
    const isUsingOldVersion =
      currentQuestionStr &&
      executionQuestionStr &&
      currentQuestionStr !== executionQuestionStr

    if (userProvidedPrompt) {
      sections.push({
        label: "Question",
        content: (
          <div
            className={cn(
              "text-sm leading-relaxed whitespace-pre-wrap break-words",
              isUsingOldVersion && "text-coval-warning-text",
            )}
          >
            {userProvidedPrompt}
          </div>
        ),
      })
    }

    const explanation = getMetricResultString(mo, "explanation")
    const confidence = getMetricConfidence(mo)
    if (confidence) {
      sections.push({
        label: "Confidence",
        content: <div className="text-sm capitalize">{confidence}</div>,
      })
    }
    if (explanation) {
      sections.push({
        label: "Explanation",
        content: (
          <div className="text-sm whitespace-pre-wrap break-words">
            {explanation}
          </div>
        ),
      })
    }

    return appendJudgeConfidence(sections.length > 0 ? sections : null, mo, {
      binary: true,
    })
  }

  hasCustomVotingUI = true

  renderHumanFeedbackValueButton(
    annotation: Annotation | null,
    onValueChange: (
      floatValue: number | null,
      stringValue: string | null,
      setValue: string[] | null,
    ) => void,
    onExplanationChange: (explanation: string | null) => void,
    otherUserAnnotations?: Annotation[],
  ): React.ReactNode {
    return (
      <BinaryHumanFeedbackButton
        annotation={annotation}
        onValueChange={onValueChange}
        onExplanationChange={onExplanationChange}
        otherUserAnnotations={otherUserAnnotations}
        targetCondition={this.getEffectiveTargetCondition()}
      />
    )
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
    textAreaRef?: React.RefObject<HTMLTextAreaElement>,
  ): React.ReactNode {
    const isAudioMetric =
      metricRecordData.metric_manager === "manager_audio_llm_judge" ||
      metricRecordData.metric_manager === "manager_audio_llm_binary"
    const placeholder = isAudioMetric
      ? "Enter a yes/no question to evaluate your audio"
      : "Enter a yes/no question to evaluate your transcript"

    const showTranscriptFilter = supportsTranscriptFilter(metricRecordData)
    const showModelSelection = supportsModelSelection(metricRecordData)
    const showTraceContext = supportsTraceContext(metricRecordData)

    return (
      <div className="space-y-6">
        <QuestionTextarea
          metricRecordData={metricRecordData}
          onChange={onChange}
          label="Metric Prompt"
          required
          placeholder={`${placeholder}\n\nExample: "Did the agent greet the customer politely?"\n\nThe LLM will answer YES, NO, or UNKNOWN based on analyzing the conversation against your question.`}
          textAreaRef={textAreaRef}
        />
        {showTraceContext && (
          <TraceContextToggle
            metricRecordData={metricRecordData}
            onChange={onChange}
          />
        )}
        <div className="grid grid-cols-3 gap-6">
          <KnowledgeBaseDisableControl
            metricRecordData={metricRecordData}
            onChange={onChange}
          />
          {showModelSelection && (
            <LLMModelSelect
              metricRecordData={metricRecordData}
              onChange={onChange}
              supportsCovalJudge
            />
          )}
          {showTranscriptFilter && (
            <TranscriptFilterToggle
              metricRecordData={metricRecordData}
              onChange={onChange}
            />
          )}
        </div>
        {showTranscriptFilter && (
          <TranscriptFilterDetails
            metricRecordData={metricRecordData}
            onChange={onChange}
          />
        )}
        <CovalJudgeConfidenceInfo metricRecordData={metricRecordData} />
        <TargetFilterToggle
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
      </div>
    )
  }

  getKeyboardShortcutHints(): { key: string; label: string }[] {
    return [
      { key: "Y", label: "Yes" },
      { key: "N", label: "No" },
      { key: "X", label: "N/A" },
    ]
  }

  renderAssignmentsTableReviewCell(
    simulationOutputId: string,
    annotations_for_metric: Annotation[],
    projectId?: string,
  ): React.ReactNode {
    return (
      <HumanReviewInputWrapper
        metricId={this.id || ""}
        simulationOutputId={simulationOutputId}
        annotations_for_metric={annotations_for_metric}
        projectId={projectId}
        targetCondition={this.getEffectiveTargetCondition()}
      />
    )
  }

  renderAssignmentsTableAIResultCell(
    simulationOutputRow: RunRowLike,
    metricId: string,
  ): React.ReactNode {
    return (
      <AIReviewButton
        metricId={metricId}
        simulationOutputRow={simulationOutputRow}
        targetCondition={this.getEffectiveTargetCondition()}
      />
    )
  }
}
